import {
  AnalyticsSessionPageViewSchema,
  AnalyticsSessionsPageSchema,
  AnalyticsSummarySchema,
  AnalyticsUsersPageSchema,
  type AnalyticsAppFilter,
  type AnalyticsSessionPageView,
  type AnalyticsSessionsPage,
  type AnalyticsSummary,
  type AnalyticsUsersPage,
} from '@carsfinders/contracts';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { apiFetch } from '../../lib/api';

/**
 * «Аналітика» data layer — apps/api `AdminAnalyticsController`:
 *
 *   GET /api/admin/analytics/summary?from=&to=&app=            -> AnalyticsSummary
 *   GET /api/admin/analytics/sessions?from=&to=&app=&registeredOnly=&includeStaff=&userId=&cursor=&limit=
 *                                                              -> { items, nextCursor }
 *   GET /api/admin/analytics/sessions/:id/pages                -> AnalyticsSessionPageView[]
 *   GET /api/admin/analytics/users?cursor=&limit=              -> { items, nextCursor }
 *
 * Every answer goes through its contract schema (same rule as finance/api.ts):
 * a shape the screen cannot render fails loudly here, not as `undefined` deep
 * inside a chart.
 */

export interface AnalyticsSummaryParams {
  from: string;
  to: string;
  app: AnalyticsAppFilter;
}

export interface AnalyticsSessionsParams {
  from?: string;
  to?: string;
  app: AnalyticsAppFilter;
  registeredOnly: boolean;
  includeStaff: boolean;
  userId?: string;
  limit?: number;
}

const ROOT = 'admin/analytics';

export async function getAnalyticsSummary(q: AnalyticsSummaryParams): Promise<AnalyticsSummary> {
  const payload = await apiFetch<unknown>(`${ROOT}/summary`, {
    searchParams: { from: q.from, to: q.to, app: q.app },
  });
  return AnalyticsSummarySchema.parse(payload);
}

export async function getAnalyticsSessions(
  q: AnalyticsSessionsParams & { cursor?: string },
): Promise<AnalyticsSessionsPage> {
  const payload = await apiFetch<unknown>(`${ROOT}/sessions`, {
    searchParams: {
      from: q.from,
      to: q.to,
      app: q.app,
      // Omitted rather than `false`: the server's default is already off.
      registeredOnly: q.registeredOnly ? 'true' : undefined,
      includeStaff: q.includeStaff ? 'true' : undefined,
      userId: q.userId,
      cursor: q.cursor,
      limit: q.limit !== undefined ? String(q.limit) : undefined,
    },
  });
  return AnalyticsSessionsPageSchema.parse(payload);
}

export async function getAnalyticsSessionPages(id: string): Promise<AnalyticsSessionPageView[]> {
  const payload = await apiFetch<unknown>(`${ROOT}/sessions/${encodeURIComponent(id)}/pages`);
  return AnalyticsSessionPageViewSchema.array().parse(payload);
}

export async function getAnalyticsUsers(cursor?: string): Promise<AnalyticsUsersPage> {
  const payload = await apiFetch<unknown>(`${ROOT}/users`, { searchParams: { cursor } });
  return AnalyticsUsersPageSchema.parse(payload);
}

export const ANALYTICS_QUERY_KEY = ['admin', 'analytics'] as const;

/** react-query's next page param: the server cursor, `undefined` on the last page. */
export function nextPageParam(last: { nextCursor: string | null }): string | undefined {
  return last.nextCursor ?? undefined;
}

export function useAnalyticsSummary(q: AnalyticsSummaryParams) {
  return useQuery({
    queryKey: [...ANALYTICS_QUERY_KEY, 'summary', q.from, q.to, q.app],
    queryFn: () => getAnalyticsSummary(q),
  });
}

/** The session feed, paged by the server's `nextCursor`. */
export function useAnalyticsSessions(q: AnalyticsSessionsParams) {
  return useInfiniteQuery({
    queryKey: [
      ...ANALYTICS_QUERY_KEY,
      'sessions',
      q.from ?? null,
      q.to ?? null,
      q.app,
      q.registeredOnly,
      q.includeStaff,
      q.userId ?? null,
      q.limit ?? null,
    ],
    queryFn: ({ pageParam }) => getAnalyticsSessions({ ...q, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextPageParam,
  });
}

/** One session's page views — fetched only once its row is expanded. */
export function useAnalyticsSessionPages(id: string, enabled: boolean) {
  return useQuery({
    queryKey: [...ANALYTICS_QUERY_KEY, 'session-pages', id],
    queryFn: () => getAnalyticsSessionPages(id),
    enabled,
  });
}

/** Registered users by last visit, paged by `nextCursor`. */
export function useAnalyticsUsers() {
  return useInfiniteQuery({
    queryKey: [...ANALYTICS_QUERY_KEY, 'users'],
    queryFn: ({ pageParam }) => getAnalyticsUsers(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextPageParam,
  });
}
